import React, { useEffect, useState, useMemo, ChangeEvent, FocusEvent } from "react";
import { Combobox } from "@headlessui/react";
import debounce from "common/debounce";
import Input from "components/Input";
import clsx from "clsx";
import styles from "./style.module.scss";
import { searchCompanyAPI } from "services/api/haulpay";
import { ICompany } from "models/HaulPay";
import { FieldError } from "react-hook-form";
import BillToCompanyItem from "features/main/HaulPay/Carrier/RequestPay/Information/modules/BillToCompanyItem";
import AddCustomerModal from "features/main/HaulPay/modules/Shared/AddCustomerModal";
import { useToggle } from "usehooks-ts";
import Button from "../Button";
import useDeviceType from "../../hooks/useDeviceType";
import Icon from "@mdi/react";
import { mdiMagnify } from "@mdi/js";
import Modal from "../Modal";

interface DebtorSearchProps {
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  onSelect: (company: ICompany) => void;
  error?: FieldError;
  label?: string | React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  showAddNewCustomer?: boolean;
  isConnectedDebtor?: boolean;
}

const DebtorSearch: React.FC<DebtorSearchProps> = ({
  name,
  value,
  onChange,
  onSelect,
  error,
  label,
  required = false,
  disabled = false,
  placeholder = "",
  className,
  showAddNewCustomer = true,
  isConnectedDebtor = false
}) => {
  const [searchString, setSearchString] = useState<string | undefined>(value);
  const [filteredData, setFilteredData] = useState<ICompany[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [isAddCustomerModalOpen, toggleAddCustomerModal] = useToggle();
  const { isDesktop } = useDeviceType();

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        if (!query.trim()) {
          setFilteredData([]);
          setIsDropdownVisible(false);
          return;
        }
        setIsLoading(true);
        try {
          const results = await searchCompanyAPI(query, "", { is_connected: isConnectedDebtor });
          setFilteredData(results?.data?.results || []);
          setIsDropdownVisible(true);
        } catch (error) {
          setFilteredData([]);
          setIsDropdownVisible(false);
        } finally {
          setIsLoading(false);
        }
      }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    value && setSearchString(value);
  }, [value]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchString(inputValue);
    onChange && onChange(inputValue);
    debouncedSearch(inputValue);
  };

  const handleSelect = (company: ICompany) => {
    if (company) {
      setSearchString(company.dba);
      onSelect(company);
      setIsDropdownVisible(false);
    }
  };

  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.relatedTarget?.className.includes("company-btn")) {
      setIsDropdownVisible(false);
    }
  };

  if (isDesktop) {
    return (
      <>
        <div className={clsx(styles.smartInputContainer, className)} onBlur={handleBlur}>
          <Combobox
            name={name}
            value={filteredData.find((item) => item.dba === searchString) || null}
            onChange={handleSelect}>
            <div className={clsx(styles.smartInputWrapper, "relative")}>
              <Combobox.Input
                as={Input}
                value={searchString}
                name={name}
                label={label}
                onChange={handleInputChange}
                placeholder={placeholder}
                readOnly={disabled}
                error={error}
                isLoading={isLoading}
                required={required}
              />

              {isDropdownVisible && filteredData.length > 0 && (
                <Combobox.Options className={styles.debtorList}>
                  {filteredData.map((item) => (
                    <Combobox.Option
                      key={item.id}
                      value={item}
                      className={() => "cursor-pointer select-none relative py-2 pr-2"}>
                      {() => <BillToCompanyItem key={item.id} company={item} />}
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              )}

              {isDropdownVisible && filteredData.length === 0 && !isLoading && (
                <Combobox.Options className={styles.debtorList}>
                  <Combobox.Option
                    value={null}
                    className={() => "cursor-pointer select-none relative py-2 pr-2"}>
                    {() => (
                      <div className={styles.noList}>
                        <p>You didn’t find the company? </p>
                        <br />
                        <button
                          type="button"
                          className="underline text-info company-btn"
                          onClick={toggleAddCustomerModal}>
                          Click here to add
                        </button>
                      </div>
                    )}
                  </Combobox.Option>
                </Combobox.Options>
              )}
            </div>
          </Combobox>
        </div>
        <AddCustomerModal
          isAddModalOpen={isAddCustomerModalOpen}
          toggleAddModal={toggleAddCustomerModal}
          onSubmitCallback={(item: ICompany) => {
            handleSelect(item);
          }}
        />
      </>
    );
  }

  return (
    <>
      <Input
        name={name}
        value={searchString}
        label={label}
        error={error}
        onClick={() => !isDropdownVisible && setIsDropdownVisible(true)}
        disabled={disabled}
        required={required}
        readOnly
      />
      <Modal
        isOpen={isDropdownVisible}
        closeModal={() => setIsDropdownVisible(false)}
        isDisableCloseModal
        modalContainerClass="bg-background"
        dialogClass="w-full h-full sm:w-auto sm:h-auto"
        wrapperClass="flex items-center justify-center h-screen !py-0">
        <div className="flex flex-col h-full sm:h-auto">
          <span className="text-placeholder">Search Shipper/Debtor</span>
          <div className="flex-1 overflow-y-auto">
            <div className={styles.searchShipperDebtorContainer}>
              <Input
                value={searchString}
                isLoading={isLoading}
                isShowClose
                onChange={handleInputChange}
                prefixNode={<Icon path={mdiMagnify} className={styles.searchIcon} />}
                className="w-full mb-4"
                placeholder="Type to search..."
              />
            </div>
            {filteredData.length ? (
              filteredData.map((company) => (
                <BillToCompanyItem
                  key={company.id}
                  company={company}
                  onSelectCompany={() => handleSelect(company)}
                />
              ))
            ) : showAddNewCustomer ? (
              <div className={clsx(styles.noListText, "text-center mt-4")}>
                <p>You didn’t find the company? </p>
                <a
                  onClick={() => {
                    setIsDropdownVisible(false);
                    toggleAddCustomerModal();
                  }}
                  className="underline text-info">
                  Click here to add
                </a>
              </div>
            ) : null}
          </div>
          <Button
            type="button"
            className="w-full mt-4 text-white bg-red-500 rounded-lg h-14 sm:w-auto hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400"
            onClick={() => setIsDropdownVisible(false)}>
            Cancel
          </Button>
        </div>
      </Modal>
      <AddCustomerModal
        isAddModalOpen={isAddCustomerModalOpen}
        toggleAddModal={toggleAddCustomerModal}
        onSubmitCallback={(item: ICompany) => {
          handleSelect(item);
        }}
      />
    </>
  );
};

export default DebtorSearch;
